import { useMemo } from 'react';
import type { Item } from '@/common/api/types/items';
import { useCreateDependencyMutation } from '@/features/items/mutations/useCreateDependencyMutation';
import { useDeleteDependencyMutation } from '@/features/items/mutations/useDeleteDependencyMutation';
import { createEditableDependencyAdapter } from '../adapters/dependencies/createEditableDependencyAdapter';
import { createReadOnlyDependencyAdapter } from '../adapters/dependencies/createReadOnlyDependencyAdapter';
import type { DependencyTabAdapter } from '../adapters/types';

interface UseDependencyTabAdapterOptions {
  editable: boolean;
}

export const useDependencyTabAdapter = (
  item: Item | undefined,
  { editable }: UseDependencyTabAdapterOptions,
): DependencyTabAdapter | null => {
  const { mutateAsync: addDependencyAsync } = useCreateDependencyMutation({
    currentItemId: item?.id ?? 0,
  });
  const { mutateAsync: removeDependencyAsync } = useDeleteDependencyMutation({
    currentItemId: item?.id ?? 0,
  });

  return useMemo(() => {
    if (!item) {
      return null;
    }

    if (!editable) {
      return createReadOnlyDependencyAdapter({ item });
    }

    return createEditableDependencyAdapter({
      item,
      loopId: item.loopId,
      canAddDependency: () => true,
      onAddDependency: async (predecessorId, successorId) => {
        await addDependencyAsync({ predecessorId, successorId });
      },
      onRemoveDependency: async (predecessorId, successorId) => {
        await removeDependencyAsync({ predecessorId, successorId });
      },
    });
  }, [item, editable, addDependencyAsync, removeDependencyAsync]);
};
